import { type ReactNode, createContext, useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { WALLET_TRACKER_ENABLED, type WalletTrackerNotification } from '../../api/userland'
import { type UseWalletTracker, useWalletTracker } from '../../hooks/useWalletTracker'
import { formatCompact } from '../../lib/format'
import { primaryPairPath } from '../../lib/pairRoutes'
import { rawAddressToFriendlyBounceable } from '../../lib/tonRawAddress'
import { useOptionalAccountSession } from '../../wallet/AccountSessionContext'
import type { WsMessage } from '../../ws/WsClient'
import { useWsOptional } from '../../ws/WsContext'
import { walletAlertDurationMs, walletAlertToastsOff } from '../settings/settingsStore'
import { usePixelToast } from '../ui/pixel-toast'
import { WalletAlertCard, alertTicker, alertVerb } from './WalletAlertCard'

const WalletTrackerContext = createContext<UseWalletTracker | null>(null)

export function useWalletTrackerContext(): UseWalletTracker {
  const value = useContext(WalletTrackerContext)
  if (!value) throw new Error('useWalletTrackerContext must be used inside WalletTrackerProvider')
  return value
}

export function useWalletTrackerOptional(): UseWalletTracker | null {
  return useContext(WalletTrackerContext)
}

export function walletTrackerNotificationPath(notification: WalletTrackerNotification): string {
  if (notification.token_master === 'native') return '/tokens/native'
  // Notifications carry the raw 0:hex master; pair routes expect the same
  // friendly form Discover/search navigate with, so normalize before routing.
  // The local encoder (byte-equivalent to @ton/core, pinned by
  // tonRawAddress.test.ts) keeps the TON stack out of the boot chunk.
  return primaryPairPath(
    rawAddressToFriendlyBounceable(notification.token_master) ?? notification.token_master
  )
}

export function walletTrackerAmountLabel(notification: WalletTrackerNotification): string {
  const amount = Number(notification.token_amount)
  return Number.isFinite(amount) ? formatCompact(amount) : notification.token_amount
}

export function walletTrackerUsdLabel(notification: WalletTrackerNotification): string | null {
  return typeof notification.value_usd === 'number' && notification.value_usd > 0
    ? `$${formatCompact(notification.value_usd)}`
    : null
}

// глагол и тикер живут в карточке алерта (единая формулировка для
// всплывающего алерта и ленты уведомлений)
export { alertVerb, alertTicker }

function isWalletTrackerNotification(value: unknown): value is WalletTrackerNotification {
  if (!value || typeof value !== 'object') return false
  const item = value as Partial<WalletTrackerNotification>
  return (
    Number.isSafeInteger(item.sequence) &&
    typeof item.id === 'string' &&
    typeof item.wallet_address === 'string' &&
    typeof item.token_master === 'string' &&
    typeof item.token_amount === 'string' &&
    (item.action === 'buy' || item.action === 'sell' || item.action === 'transfer')
  )
}

export function WalletTrackerProvider({
  children,
  enabled = WALLET_TRACKER_ENABLED,
}: {
  children: ReactNode
  enabled?: boolean
}) {
  if (!enabled) return <>{children}</>
  return <EnabledWalletTrackerProvider>{children}</EnabledWalletTrackerProvider>
}

function EnabledWalletTrackerProvider({ children }: { children: ReactNode }) {
  // Re-read on sign-in / account switch: the channel is per account.
  const accountKey = useOptionalAccountSession()?.me?.account_id ?? null
  const tracker = useWalletTracker(true, accountKey)
  const client = useWsOptional()
  const { push } = usePixelToast()
  const navigate = useNavigate()
  const { status, channel, acceptLive, reseed, updateNotification, rules, updateRule } = tracker
  // правила нужны колоколу алерта (мьют кошелька) — читаем через ref,
  // чтобы подписка на WS не пересоздавалась на каждое обновление списка
  const rulesRef = useRef(rules)
  rulesRef.current = rules

  useEffect(() => {
    if (!client || status !== 'ready' || !channel) return
    const onActivity = (message: WsMessage<WalletTrackerNotification>) => {
      if (message.event !== 'wallet.activity' || !isWalletTrackerNotification(message.data)) return
      const result = acceptLive(message.data)
      if (!result.accepted) return
      if (result.gap) void reseed(result.previousSequence).catch(() => {})
      const notification = message.data
      // "Display notifications" off, or "Mute while inactive" on and nobody
      // looking: the alert is in the inbox already (acceptLive above), only
      // its toast is held back
      if (walletAlertToastsOff()) return
      // Алерт — пиксельная карточка (финал лабы #196), стек общий с
      // тостами исполнения; где он стоит и сколько живёт — настройки
      // аккаунта (сверху по центру и 3.0s, пока не заданы).
      const rule =
        rulesRef.current.find((r) => r.id === notification.tracked_wallet_id) ??
        rulesRef.current.find((r) => r.address === notification.wallet_address)
      push({
        tone: 'ok',
        kind: 'alert',
        duration: walletAlertDurationMs(),
        content: ({ dismiss }) => (
          <WalletAlertCard
            notification={notification}
            onOpen={() => {
              void updateNotification(notification.id, { read: true }).catch(() => {})
              navigate(walletTrackerNotificationPath(notification))
              dismiss()
            }}
            onDismiss={dismiss}
            bell={
              rule
                ? {
                    on: rule.enabled,
                    toggle: () => {
                      void updateRule(rule.id, { enabled: !rule.enabled }).catch(() => {})
                    },
                  }
                : undefined
            }
          />
        ),
      })
    }

    let cancelled = false
    const unsubscribe = client.subscribe(channel, onActivity)
    // REST seed → subscribe ACK → delta seed. The gateway registers the
    // subscription before emitting its ACK, so this ordering closes the entire
    // bootstrap window without racing a delta request ahead of registration.
    void client
      .waitForSubscription(channel)
      .then(() => {
        if (!cancelled) return reseed()
      })
      .catch(() => {})
    const stopReconnect = client.onReconnect(() => {
      void reseed().catch(() => {})
    })
    return () => {
      cancelled = true
      stopReconnect()
      unsubscribe()
    }
  }, [acceptLive, channel, client, navigate, push, reseed, status, updateNotification, updateRule])

  return <WalletTrackerContext.Provider value={tracker}>{children}</WalletTrackerContext.Provider>
}
